import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { messages } from '@nesc/shared';
import { api, errorMessage } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';

/** キャンペーン名の変更。状態に関わらず行える（送信には影響しない） */
export const CampaignRenameDialog = ({
  open,
  onOpenChange,
  campaignId,
  currentName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaignId: string;
  currentName: string;
}) => {
  const queryClient = useQueryClient();
  const [name, setName] = useState(currentName);

  useEffect(() => {
    if (open) setName(currentName);
  }, [open, currentName]);

  const save = useMutation({
    mutationFn: () => api.patch(`/campaigns/${campaignId}`, { name: name.trim() }),
    onSuccess: () => {
      toast.success(messages.common.saved);
      void queryClient.invalidateQueries({ queryKey: qk.campaign(campaignId) });
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      onOpenChange(false);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const trimmed = name.trim();
  const blockedReason = !trimmed
    ? 'キャンペーン名を入力してください'
    : trimmed === currentName
      ? '名前が変わっていません'
      : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{messages.campaign.rename}</DialogTitle>
          <DialogDescription>{messages.campaign.renameNote}</DialogDescription>
        </DialogHeader>

        <DialogBody>
          <Field label="キャンペーン名" required>
            {(props) => (
              <Input
                {...props}
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={80}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !blockedReason && !save.isPending) save.mutate();
                }}
              />
            )}
          </Field>
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" size="lg" onClick={() => onOpenChange(false)}>
            {messages.common.cancel}
          </Button>
          <Button
            size="lg"
            onClick={() => save.mutate()}
            disabled={save.isPending || !!blockedReason}
            disabledReason={blockedReason ?? undefined}
          >
            {messages.common.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
